import { forwardRef } from 'react';

import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

interface GradientButtonProps extends Omit<ButtonProps, 'variant'> {
  loading?: boolean;
  variant?: 'primary' | 'outlined' | 'white';
}

const variantClasses = {
  primary:
    'text-white bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-dark)] hover:from-[var(--color-primary-light)] hover:to-[var(--color-primary)] disabled:opacity-50 disabled:from-muted disabled:to-muted disabled:text-muted-foreground',
  outlined:
    'bg-transparent text-foreground border border-border hover:border-primary hover:bg-primary/10',
  white:
    'bg-background text-primary shadow-md hover:bg-accent dark:bg-white dark:text-primary dark:hover:bg-white/90',
};

export const GradientButton = forwardRef<HTMLButtonElement, GradientButtonProps>(
  ({ loading = false, disabled, children, variant = 'primary', className, ...props }, ref) => {
    const isDisabled = disabled || loading;

    return (
      <Button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          'relative',
          variantClasses[variant],
          className
        )}
        {...props}
      >
        {loading && (
          <Spinner
            className={cn(
              'absolute',
              variant === 'white' ? 'text-primary' : 'text-white'
            )}
          />
        )}
        <span style={{ visibility: loading ? 'hidden' : 'visible' }}>{children}</span>
      </Button>
    );
  }
);

GradientButton.displayName = 'GradientButton';

export default GradientButton;
